import { useMemo, useState } from "react";
import {
  ArrowUp,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { useGetCarriers } from "@/api/carriers";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { selfRatedOnly, sortCarriers } from "@/components/carrier-filter";
import { filterAndSortCentersForCarriers } from "@/components/dispatch-center-filter";
import { useDispatchDateStore } from "@/features/dashboard/dispatch-date.store";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";
import { useDispatchTimeWindows } from "@/api/dispatch-time-windows";
import { formatWindowRange } from "@/features/dashboard/format-window";
import { operationalDateKey } from "@/lib/operational-date";
import {
  useDispatchOperationalTimeZone,
  useInitializeDispatchDate,
} from "@/features/dashboard/use-dispatch-operational-time-zone";

/**
 * Dispatch scope pills (carrier · center · date · window), styled with the v2
 * tokens and inlined like the design command bar.
 */

const AVATAR_COLORS = [
  "#4f46e5",
  "#16a34a",
  "#d97706",
  "#db2777",
  "#0891b2",
  "#7c3aed",
  "#0d9488",
];

const pillBase =
  "inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[13px] font-semibold transition-colors";
const pillIdle = "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50";
const pillActive = "border-indigo-200 bg-indigo-50 text-indigo-700";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function CarriersPill() {
  const { carrierIds, setCarrierIds } = useCarrierScope();
  const { data } = useGetCarriers();
  const owned = useMemo(
    () => sortCarriers(selfRatedOnly(data?.data ?? [])),
    [data]
  );
  const [open, setOpen] = useState(false);
  const total = owned.length;
  const selectedCount = carrierIds.length;
  const allSelected = total > 0 && selectedCount === total;
  const none = selectedCount === 0;
  const selected = owned.filter((c) => carrierIds.includes(c.id));
  const label = none
    ? "No carriers"
    : allSelected
      ? `All ${total} carriers`
      : selectedCount === 1
        ? (selected[0]?.name ?? "1 carrier")
        : `${selectedCount} carriers`;
  const toggle = (id: string) =>
    setCarrierIds(
      carrierIds.includes(id)
        ? carrierIds.filter((x) => x !== id)
        : [...carrierIds, id]
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`${pillBase} pl-1.5 ${none ? "border-amber-300 bg-white text-amber-700" : pillIdle}`}
          aria-label={label}
          title={label}
        >
          <div className="flex items-center -space-x-1.5">
            {selected.length === 0 ? (
              <div
                className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold text-white ring-2 ring-white"
                style={{ background: "#d4d4d8" }}
              >
                ?
              </div>
            ) : (
              selected.slice(0, 3).map((c, i) => (
                <div
                  key={c.id}
                  className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold text-white ring-2 ring-white"
                  style={{
                    background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                  }}
                  title={c.name}
                >
                  {initials(c.name)}
                </div>
              ))
            )}
          </div>
          <span className="hidden 2xl:inline">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-2">
        <div className="flex items-center justify-between gap-2 border-b border-zinc-100 px-2 pb-2">
          <button
            className="text-[11.5px] font-semibold text-indigo-600 hover:underline disabled:text-zinc-400"
            disabled={allSelected || total === 0}
            onClick={() => setCarrierIds(owned.map((c) => c.id))}
          >
            Select all
          </button>
          <button
            className="text-[11.5px] font-semibold text-zinc-500 hover:underline disabled:text-zinc-400"
            disabled={none}
            onClick={() => setCarrierIds([])}
          >
            Clear
          </button>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {owned.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-zinc-100"
            >
              <Checkbox
                checked={carrierIds.includes(c.id)}
                onCheckedChange={() => toggle(c.id)}
              />
              <span className="flex-1 truncate text-[13px]">{c.name}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CenterPill() {
  const { carrierIds, dispatchCenterIds, setDispatchCenterIds } =
    useCarrierScope();
  const { data } = useGetDispatchCenters();
  const centers = useMemo(
    () => filterAndSortCentersForCarriers(data ?? [], carrierIds),
    [data, carrierIds]
  );
  const [open, setOpen] = useState(false);
  if (carrierIds.length === 0) return null;

  const total = centers.length;
  const selectedCount = dispatchCenterIds.length;
  const allSelected = total > 0 && selectedCount === total;
  const none = selectedCount === 0;
  const label =
    none || allSelected
      ? "All centers"
      : selectedCount === 1
        ? (centers.find((c) => c.id === dispatchCenterIds[0])?.name ??
          "1 center")
        : `${selectedCount} centers`;
  const toggle = (id: string) =>
    setDispatchCenterIds(
      dispatchCenterIds.includes(id)
        ? dispatchCenterIds.filter((x) => x !== id)
        : [...dispatchCenterIds, id]
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`${pillBase} ${pillIdle}`}
          aria-label={label}
          title={label}
        >
          <MapPin className="h-4 w-4 text-zinc-400" />
          <span className="hidden 2xl:inline">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-2">
        <div className="flex items-center justify-between gap-2 border-b border-zinc-100 px-2 pb-2">
          <button
            className="text-[11.5px] font-semibold text-indigo-600 hover:underline disabled:text-zinc-400"
            disabled={allSelected || total === 0}
            onClick={() => setDispatchCenterIds(centers.map((c) => c.id))}
          >
            Select all
          </button>
          <button
            className="text-[11.5px] font-semibold text-zinc-500 hover:underline disabled:text-zinc-400"
            disabled={none}
            onClick={() => setDispatchCenterIds([])}
          >
            Clear
          </button>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {centers.length === 0 ? (
            <div className="px-2 py-3 text-[12px] text-zinc-500">
              No centers for the selected carriers.
            </div>
          ) : (
            centers.map((c) => (
              <label
                key={c.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-zinc-100"
              >
                <Checkbox
                  checked={dispatchCenterIds.includes(c.id)}
                  onCheckedChange={() => toggle(c.id)}
                />
                <span className="flex-1 truncate text-[13px]">{c.name}</span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DatePill() {
  const { selectedDate, setSelectedDate, stepDays, resetToToday } =
    useDispatchDateStore();
  const operationalTimeZone = useDispatchOperationalTimeZone();
  useInitializeDispatchDate(operationalTimeZone);
  const [open, setOpen] = useState(false);
  const today = useMemo(
    () => operationalDateKey(new Date(), operationalTimeZone),
    [operationalTimeZone]
  );
  if (!operationalTimeZone || !selectedDate || !today) {
    return (
      <div
        role="status"
        className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 text-[12px] font-semibold text-amber-800"
        title="Choose a dispatch center with one operational timezone before selecting a day"
      >
        <Calendar className="h-4 w-4" />
        Select one dispatch center
      </div>
    );
  }
  const isToday = selectedDate === today;
  const dt = new Date(`${selectedDate}T12:00:00Z`);
  const base = dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const label = isToday ? `Today · ${base}` : base;
  const compactLabel = isToday
    ? "Today"
    : dt.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });

  return (
    <div className="inline-flex h-10 shrink-0 items-center overflow-hidden rounded-full border border-zinc-200 bg-white">
      <button
        aria-label="Previous day"
        onClick={() => stepDays(-1)}
        className="grid h-full w-8 place-items-center text-zinc-400 hover:bg-zinc-50"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            aria-label={`Select date: ${label}`}
            title={label}
            className="inline-flex h-full items-center gap-1.5 px-1.5 text-[13px] font-semibold text-zinc-800"
          >
            <Calendar className="h-4 w-4 text-zinc-400" />
            <span className="2xl:hidden">{compactLabel}</span>
            <span className="hidden 2xl:inline">{label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-auto p-3">
          <div className="flex flex-col gap-2">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) =>
                e.target.value && setSelectedDate(e.target.value)
              }
              className="h-9 rounded-md border border-zinc-200 px-2 text-[13px] focus:outline-hidden focus:ring-1 focus:ring-indigo-400"
            />
            <button
              onClick={() => {
                resetToToday(operationalTimeZone);
                setOpen(false);
              }}
              disabled={isToday}
              className="h-8 rounded-md text-[12px] font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-40"
            >
              Jump to today
            </button>
          </div>
        </PopoverContent>
      </Popover>
      <button
        aria-label="Next day"
        onClick={() => stepDays(1)}
        className="grid h-full w-8 place-items-center text-zinc-400 hover:bg-zinc-50"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

const WINDOW_OVERDUE = "overdue";

function WindowPill() {
  const { windowFilter, setWindowFilter } = useShortcutsStore();
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const { data: windows = [] } = useDispatchTimeWindows(
    carrierIds,
    dispatchCenterIds
  );
  const active = windowFilter !== null;
  const label = useMemo(() => {
    if (windowFilter === null) return "All windows";
    if (windowFilter === WINDOW_OVERDUE) return "Overdue";
    const [s, e] = windowFilter.split("__");
    return s && e ? formatWindowRange(s, e) : "All windows";
  }, [windowFilter]);

  const opt = (l: string, sel: boolean, on: () => void) => (
    <button
      onClick={on}
      className={
        "rounded-md px-2.5 py-1.5 text-left text-[13px] hover:bg-zinc-100 " +
        (sel ? "font-semibold text-zinc-900" : "text-zinc-700")
      }
    >
      {l}
    </button>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`${pillBase} ${active ? pillActive : pillIdle}`}
          aria-label={`Time window: ${label}`}
          title={label}
        >
          <Clock className="h-4 w-4 opacity-60" />
          <span className="hidden 2xl:inline">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1.5">
        <div className="flex flex-col">
          {opt("All windows", windowFilter === null, () =>
            setWindowFilter(null)
          )}
          {windows.map((w) => {
            const v = `${w.startTime}__${w.endTime}`;
            return (
              <span key={v}>
                {opt(
                  formatWindowRange(w.startTime, w.endTime),
                  windowFilter === v,
                  () => setWindowFilter(v)
                )}
              </span>
            );
          })}
          {opt("⚠ Overdue", windowFilter === WINDOW_OVERDUE, () =>
            setWindowFilter(WINDOW_OVERDUE)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ScopePillsV2() {
  return (
    <div className="flex w-max min-w-max flex-nowrap items-center gap-1.5 select-none">
      <CarriersPill />
      <CenterPill />
      {/* Date + window are PICKUP-ONLY filters — only pickup tasks carry a
          scheduled window; deliveries/dropoffs/returns have none. Group them
          under a blue "Pickups" label (the pickup lane color) so they don't
          read as global scope. */}
      <div className="flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 py-1 pl-2.5 pr-1">
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-bold uppercase tracking-wide text-blue-700">
          <ArrowUp className="h-3.5 w-3.5" strokeWidth={2.8} />
          Pickups
        </span>
        <DatePill />
        <WindowPill />
      </div>
    </div>
  );
}
